import { Close, Delete, ResetTv } from '@mui/icons-material';
import { Box, Button, IconButton, Modal, Stack } from '@mui/material';
import React, { useEffect, useMemo, } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useMessage } from '../../../../base/MessageProvider';
import ReactTextInput from '../../../../components/ControllerComponent/ReactTextInput';
import clsx from 'clsx';
import { addWorkDoneData, deleteWorkDoneData, getProfileData, updateWorkDoneData } from '../../../actions/profile';
import { Bars } from 'react-loader-spinner';
import ReactSelect from '../../../../components/ControllerComponent/ReactSelect';
import { currentYear, getYearOptions } from '../../../../utils';
import { getProjectCategoryList } from '../../../actions/general';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validateSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    year: Yup.number().required("Year is required"),
    projectType: Yup.string().required("Project type is required"),
    workLink: Yup.string()
})

const WorkDoneEditModal = ({ open, onClose }) => {

    const dispatch = useDispatch();
    const alert = useMessage();
    const loading = useSelector((state) => state.artistProfileReducer?.loading);
    const workdoneData = useSelector((state) => state.artistProfileReducer?.selectedWorkDone);
    const categoriesList = useSelector(state => state.artistGeneralReducer?.projectCategoryList) ?? [];

    const projectCategoryOptions = useMemo(() => categoriesList?.map((item) => ({
        label: item.name,
        value: item.id
    })), [categoriesList]);

    const {
        control, handleSubmit,
        setValue, watch,
        clearErrors, reset,
        formState: { errors }
    } = useForm({
        defaultValues: {
            name: '',
            year: currentYear.toString(),
            projectType: "",
        },
        resolver: yupResolver(validateSchema)
    });

    useEffect(() => {
        clearErrors(); reset();
        dispatch(getProjectCategoryList());
        if (workdoneData) {
            setValue('name', workdoneData?.name ?? '')
            setValue('year', workdoneData?.year);
            setValue('projectType', workdoneData?.projectCategory?.id ?? "");
        }
    }, [workdoneData]);

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
        onClose();
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const addHandler = async (data) => {
        dispatch(addWorkDoneData({ ...data }, successCallback, errorCallback));
    }

    const updateHandler = async (data) => {
        dispatch(updateWorkDoneData({ ...data, id: workdoneData?.id }, successCallback, errorCallback));
    }

    const deleteHandler = async () => {
        dispatch(deleteWorkDoneData({ workdoneId: workdoneData?.id }, successCallback, errorCallback));
    }

    const onClickDeleteButton = () => {
        alert.showConfirmation2({
            message: 'Are you sure you want to delete?',
            button1Title: 'Cancel',
            button2Title: 'Delete',
            onPressBtn1: () => {},
            onPressBtn2: deleteHandler,
        });
    }

    const onSubmit = async (data) => {
        if (workdoneData) updateHandler(data);
        else addHandler(data);
    }

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-6">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-center text-[16px]">{!workdoneData ? 'Add' : 'Edit'} Experience</h2>
                </Box>
                <Stack className="w-full" my={1} spacing={2}>
                    <ReactTextInput
                        hookFormControl={control}
                        formLabel="Name*"
                        varName="name"
                        key={'name'}
                        placeholder="Eg. Shortfilm"
                        errorMessage={errors?.name?.message}
                    />
                    <ReactSelect
                        hookFormControl={control}
                        formLabel="Year*"
                        varName="year"
                        key={'year'}
                        options={getYearOptions()}
                        errorMessage={errors?.year?.message}
                    />
                    <ReactSelect
                        hookFormControl={control}
                        formLabel="Project Type*"
                        varName="projectType"
                        key={'projectType'}
                        options={projectCategoryOptions}
                        placeholder="Select option"
                        errorMessage={errors?.projectType?.message}
                    />
                </Stack>
                <Stack className="w-full" alignItems={'center'} direction={'row'} spacing={1}>
                    <Button onClick={handleSubmit(onSubmit)} disabled={loading} className={clsx("bg-[#2F80ED] flex gap-3 justify-center text-[#FFF] w-full rounded-full my-2 font-['Poppins-Medium'] font-[500]", { "bg-opacity-70": loading })}>
                        Submit
                        <Bars
                            height="20"
                            width={'20'}
                            color="#FFFFFF"
                            ariaLabel="bars-loading"
                            visible={loading}
                        />
                    </Button>
                    {workdoneData && (
                        <IconButton onClick={onClickDeleteButton} size='small' className="bg-[#e02f2f] w-[39px] h-[39px]">
                            <Delete className='text-white text-xl' />
                        </IconButton>
                    )}
                </Stack>
            </Box>
        </Modal>
    );
}

export default React.memo(WorkDoneEditModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});